import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { apiClient } from '../../core/api/client';
import { useAuth } from '../../core/hooks/useAuth';
import { useTheme } from '../../core/theme/ThemeContext';
import {
  FaTachometerAlt,
  FaBell,
  FaShoppingCart,
  FaStickyNote,
  FaCalendarAlt,
  FaCog,
  FaTools,
  FaSignOutAlt,
  FaUser,
  FaBars,
  FaTimes,
  FaMousePointer,
  FaUsers,
  FaStore,
  FaHistory,
  FaKey,
  FaTags,
  FaChevronDown,
  FaChevronRight,
  FaTasks,
  FaMoon,
  FaSun,
  FaBitcoin
} from 'react-icons/fa';
import './Layout.css';

function Layout({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, permissions, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [newShopOrdersCount, setNewShopOrdersCount] = useState(0);
  const [settingsDropdownOpen, setSettingsDropdownOpen] = useState(false);

  const username = user?.username;
  
  // Fetch new shop orders count
  useEffect(() => {
    const fetchNewOrdersCount = async () => {
      try {
        // Use the same endpoint with source=obchod to get all shop orders
        const response = await apiClient.get('/orders?source=obchod');
        const newShopOrders = response.data.filter(order =>
          order.status === 'new'
        );
        setNewShopOrdersCount(newShopOrders.length);
      } catch (error) {
        console.error('Error fetching orders count:', error);
      }
    };

    fetchNewOrdersCount();
    // Refresh count every 30 seconds
    const interval = setInterval(fetchNewOrdersCount, 30000);

    // Listen for manual updates
    const handleShopOrdersUpdate = () => {
      fetchNewOrdersCount();
    };
    window.addEventListener('shopOrdersUpdated', handleShopOrdersUpdate);

    return () => {
      clearInterval(interval);
      window.removeEventListener('shopOrdersUpdated', handleShopOrdersUpdate);
    };
  }, []);

  const mainMenuItems = [
    { path: '/dashboard', name: 'Dashboard', icon: FaTachometerAlt, permission: 'dashboard' },
    { path: '/todolist', name: 'Úlohy', icon: FaTasks, permission: 'todos' },
    { path: '/reminders', name: 'Pripomienky', icon: FaBell, permission: 'reminders' },
    { path: '/orders', name: 'Objednávky', icon: FaShoppingCart, permission: 'orders' },
    { path: '/shop-orders', name: 'Obchod/Objednávky', icon: FaStore, permission: 'shop_orders' },
    { path: '/sashe-orders', name: 'Sashe Objednávky', icon: FaShoppingCart, permission: 'orders' },
    { path: '/sales', name: 'Obchod/Predaj', icon: FaStore, permission: 'sales' },
    { path: '/sashe', name: 'Sashe-klikač', icon: FaMousePointer, permission: 'sashe' },
    { path: '/sashe-history', name: 'Sashe História', icon: FaHistory, permission: 'sashe' },
    { path: '/crypto', name: 'Crypto Portfolio', icon: FaBitcoin, permission: 'crypto' },
  ];
  
  const settingsSubmenuItems = [
    { path: '/settings', name: 'Všeobecné', icon: FaCog, permission: 'settings' },
    { path: '/users', name: 'Používatelia', icon: FaUsers, permission: 'is_admin' },
    { path: '/activity-logs', name: 'Aktivitné logy', icon: FaHistory, permission: 'is_admin' },
    { path: '/sashe-settings', name: 'Sashe nastavenia', icon: FaKey, permission: 'is_admin' },
  ];
  
  // Filter menu items based on permissions
  const menuItems = mainMenuItems.filter(item => {
    if (!item.permission) return true;
    return permissions?.[item.permission] === true;
  });

  const visibleSettingsItems = settingsSubmenuItems.filter(item => {
    if (!item.permission) return true;
    return permissions?.[item.permission] === true;
  });

  const hasSettingsAccess = visibleSettingsItems.length > 0;

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  return (
    <div className="layout-container">
      <motion.aside 
        className={`sidebar ${isSidebarOpen ? 'open' : 'closed'}`}
        animate={{ width: isSidebarOpen ? 250 : 80 }}
        transition={{ duration: 0.3 }}
      >
        <div className="sidebar-header">
          <AnimatePresence>
            {isSidebarOpen && (
              <motion.h2
                className="sidebar-title"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                GBLS UI
              </motion.h2>
            )}
          </AnimatePresence>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <button
              className="sidebar-toggle"
              onClick={toggleTheme}
              title={theme === 'dark' ? 'Prepnúť na svetlú tému' : 'Prepnúť na tmavú tému'}
            >
              {theme === 'dark' ? <FaSun /> : <FaMoon />}
            </button>
            <button className="sidebar-toggle" onClick={toggleSidebar}>
              {isSidebarOpen ? <FaTimes /> : <FaBars />}
            </button>
          </div>
        </div>

        <nav className="sidebar-nav">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`nav-item ${isActive ? 'active' : ''}`}
              >
                <motion.div
                  className="nav-item-content"
                  whileHover={{ scale: 1.05, x: 5 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Icon className="nav-icon" />
                  {!isSidebarOpen && item.path === '/shop-orders' && newShopOrdersCount > 0 && (
                    <span style={{
                      position: 'absolute',
                      top: '5px',
                      right: '15px',
                      background: '#ef4444',
                      color: 'white',
                      borderRadius: '50%',
                      width: '18px',
                      height: '18px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '0.7rem',
                      fontWeight: 'bold'
                    }}>
                      {newShopOrdersCount}
                    </span>
                  )}
                  <AnimatePresence>
                    {isSidebarOpen && (
                      <motion.span 
                        className="nav-text"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                      >
                        {item.name}
                        {item.path === '/shop-orders' && newShopOrdersCount > 0 && (
                          <span style={{
                            background: '#ef4444',
                            color: 'white',
                            borderRadius: '12px',
                            padding: '2px 8px',
                            fontSize: '0.75rem',
                            fontWeight: 'bold',
                            minWidth: '20px',
                            textAlign: 'center'
                          }}>
                            {newShopOrdersCount}
                          </span>
                        )}
                      </motion.span>
                    )}
                  </AnimatePresence>
                </motion.div>
                {isActive && (
                  <motion.div 
                    className="active-indicator"
                    layoutId="activeIndicator"
                    transition={{ type: "spring", stiffness: 300, damping: 30 }}
                  />
                )}
              </Link>
            );
          })}
          
          {hasSettingsAccess && (
            <>
              <div 
                className={`nav-item dropdown-header ${settingsDropdownOpen ? 'open' : ''} ${visibleSettingsItems.some(item => location.pathname === item.path) ? 'active' : ''}`}
                onClick={() => setSettingsDropdownOpen(!settingsDropdownOpen)}
                style={{ cursor: 'pointer' }}
              >
                <motion.div
                  className="nav-item-content"
                  whileHover={{ scale: 1.05, x: 5 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <FaCog className="nav-icon" />
                  <AnimatePresence>
                    {isSidebarOpen && (
                      <motion.span 
                        className="nav-text"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1, justifyContent: 'space-between' }}
                      >
                        <span>Nastavenia</span>
                        {settingsDropdownOpen ? <FaChevronDown size={12} /> : <FaChevronRight size={12} />}
                      </motion.span>
                    )}
                  </AnimatePresence>
                </motion.div>
              </div>
              
              <AnimatePresence>
                {settingsDropdownOpen && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    style={{ overflow: 'hidden' }}
                  >
                    {visibleSettingsItems.map(item => {
                      const Icon = item.icon;
                      const isActive = location.pathname === item.path;
                      
                      return (
                        <Link
                          key={item.path}
                          to={item.path}
                          className={`nav-item submenu-item ${isActive ? 'active' : ''}`}
                          style={{ paddingLeft: isSidebarOpen ? '40px' : '20px' }}
                        >
                          <motion.div
                            className="nav-item-content"
                            whileHover={{ scale: 1.05, x: 5 }}
                            whileTap={{ scale: 0.95 }}
                          >
                            <Icon className="nav-icon" size={16} />
                            <AnimatePresence>
                              {isSidebarOpen && (
                                <motion.span 
                                  className="nav-text"
                                  initial={{ opacity: 0 }}
                                  animate={{ opacity: 1 }}
                                  exit={{ opacity: 0 }}
                                >
                                  {item.name}
                                </motion.span>
                              )}
                            </AnimatePresence>
                          </motion.div>
                          {isActive && (
                            <motion.div 
                              className="active-indicator"
                              layoutId="activeIndicator"
                              transition={{ type: "spring", stiffness: 300, damping: 30 }}
                            />
                          )}
                        </Link>
                      );
                    })}
                  </motion.div>
                )}
              </AnimatePresence>
            </>
          )}
        </nav>

        <div className="sidebar-footer">
          <div className="user-info">
            <FaUser className="user-icon" />
            <AnimatePresence>
              {isSidebarOpen && (
                <motion.span 
                  className="username"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                >
                  {username}
                </motion.span>
              )}
            </AnimatePresence>
          </div>
          <motion.button
            className="logout-btn"
            onClick={handleLogout}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <FaSignOutAlt />
            <AnimatePresence>
              {isSidebarOpen && (
                <motion.span
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                >
                  Odhlásiť
                </motion.span>
              )}
            </AnimatePresence>
          </motion.button>
        </div>
      </motion.aside>

      <main className="main-content">
        <motion.div
          key={location.pathname}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.3 }}
        >
          {children}
        </motion.div>
      </main>
    </div>
  );
}

export default Layout;